import { useEffect, useState } from 'react';

// Single source of truth for the colour theme.
//
// - No explicit choice  -> `choice` is null, no data-theme attribute is set,
//   and CSS `prefers-color-scheme` decides (and keeps following the OS).
// - Explicit toggle      -> `choice` is 'light' | 'dark', persisted and written
//   to the data-theme attribute, which overrides the media query.
//
// Because we only set data-theme for an explicit choice, react-snap never bakes
// a theme into the pre-rendered HTML, so there is no wrong-theme flash on load.

const STORAGE_KEY = 'theme';

const getSystemTheme = () =>
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';

const getSavedChoice = () => {
    try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        return saved === 'light' || saved === 'dark' ? saved : null;
    } catch (e) {
        return null;
    }
};

const useTheme = () => {
    const [choice, setChoice] = useState(getSavedChoice); // null = follow system
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);

    const theme = choice || systemTheme;

    // Reflect an explicit choice on <html>; clear it to fall back to the OS.
    useEffect(() => {
        const de = document.documentElement;
        if (choice) de.setAttribute('data-theme', choice);
        else de.removeAttribute('data-theme');
    }, [choice]);

    // Keep following the OS while there is no explicit choice.
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const toggleTheme = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setChoice(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch (e) {
            /* ignore storage failures */
        }
    };

    return [theme, toggleTheme];
};

export default useTheme;
